import { useLayoutEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAdvertising } from '@/context/AdvertisingContext.jsx'
import { resolveAutoAds } from '@/lib/adExperiments.js'
import { autoAdsDocumentLoaded, loadAutoAds } from '@/lib/autoAdsRuntime.js'
import AutoAdsMock from './AutoAdsMock.jsx'
import { EDITORIAL_AUTO_AD_PAGES } from '@/config/adExperiments.js'

// An AdSense document cannot be safely turned back into an IDE document by
// deleting its script tag. Route exits and eligibility revocation use a reload.
export default function AutoAdsBoundary({ children }) {
  const { pathname } = useLocation()
  const policy = useAdvertising()
  const bootPath = useRef(pathname)
  const decision = useMemo(() => resolveAutoAds(policy, pathname), [policy, pathname])
  const loaded = autoAdsDocumentLoaded()
  const reset = !policy.mockEnabled && ((loaded && (pathname !== bootPath.current || !decision)) || (decision && pathname !== bootPath.current))
  useLayoutEffect(() => {
    if (reset) { window.location.replace(window.location.href); return undefined }
    if (!decision) return undefined
    loadAutoAds(policy, pathname)
    const documentNavigation = event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const anchor = event.target.closest?.('a[href]')
      if (!anchor || anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) return
      const url = new URL(anchor.href, window.location.href)
      if (url.origin === window.location.origin && url.pathname !== pathname) {
        event.preventDefault()
        event.stopPropagation()
        window.location.assign(url.href)
      }
    }
    document.addEventListener('click', documentNavigation, true)
    return () => document.removeEventListener('click', documentNavigation, true)
  }, [reset, decision, pathname, policy])
  if (reset) return null
  const previewAnchor = policy.mockEnabled && EDITORIAL_AUTO_AD_PAGES.includes(pathname) && import.meta.env.VITE_ADS_MOCK_VARIANT !== 'control'
  return <>{previewAnchor ? <div className="pb-24">{children}</div> : children}<AutoAdsMock format="anchor" /></>
}
